/**
 * EncounterSystem.ts - Random combat encounter system
 * Handles encounter chance calculation, enemy group selection, and enemy generation
 * Authentic FF1-style random battles with terrain-based encounter rates
 */

import {
  TerrainType,
  ENCOUNTER_ZONES,
  getEncounterZoneForTerrain
} from './data/WorldMapData';
import { BattleEnemy } from './types/engine';
import { getEnemySprite } from './data/EnemySprites';

// ============================================================================
// Encounter Rate Configuration
// ============================================================================

/**
 * Base encounter rates per terrain type (steps between encounters)
 * Lower = more frequent encounters
 */
const TERRAIN_ENCOUNTER_RATES: Record<TerrainType, number> = {
  [TerrainType.OCEAN]: 0, // No encounters on ocean (requires ship)
  [TerrainType.GRASS]: 25, // Moderate encounters
  [TerrainType.FOREST]: 20, // Slightly more dangerous
  [TerrainType.MOUNTAIN]: 0, // No encounters (not walkable)
  [TerrainType.DESERT]: 22, // Hot and dangerous
  [TerrainType.SWAMP]: 15, // Very dangerous! FF1 swamps are brutal
  [TerrainType.RIVER]: 0, // No encounters
  [TerrainType.ROAD]: 35, // Safest travel
  [TerrainType.BRIDGE]: 30, // Safer than grass
  [TerrainType.TOWN]: 0, // Safe zones
  [TerrainType.CASTLE]: 0, // Safe zones
  [TerrainType.CAVE]: 12, // Frequent dungeon encounters
  [TerrainType.SHRINE]: 12, // Frequent dungeon encounters
};

/**
 * Enemy stat templates - FF1-accurate stats
 * Attack, Defense, Agility, MagicDef match original NES values
 */
interface EnemyTemplate {
  id: number;
  name: string;
  baseHP: number;
  attack: number;      // Physical attack power
  defense: number;     // Physical defense
  agility: number;     // Speed / evasion
  magicDefense: number; // Magic defense
  level: number;
  expReward: number;
  gilReward: number;
}

// Basic enemy templates with combat stats
const ENEMY_TEMPLATES: Record<number, EnemyTemplate> = {
  // Early game - low stats
  1: { id: 1, name: 'Goblin', baseHP: 8, attack: 8, defense: 4, agility: 6, magicDefense: 16, level: 1, expReward: 6, gilReward: 6 },
  2: { id: 2, name: 'Wolf', baseHP: 20, attack: 12, defense: 6, agility: 12, magicDefense: 24, level: 2, expReward: 12, gilReward: 8 },
  3: { id: 3, name: 'Zombie', baseHP: 28, attack: 16, defense: 10, agility: 2, magicDefense: 20, level: 3, expReward: 24, gilReward: 12 },
  4: { id: 4, name: 'Skeleton', baseHP: 36, attack: 18, defense: 12, agility: 8, magicDefense: 24, level: 4, expReward: 36, gilReward: 18 },
  5: { id: 5, name: 'Pirate', baseHP: 44, attack: 20, defense: 8, agility: 15, magicDefense: 30, level: 5, expReward: 60, gilReward: 100 },
  6: { id: 6, name: 'Shark', baseHP: 120, attack: 28, defense: 18, agility: 24, magicDefense: 36, level: 6, expReward: 132, gilReward: 132 },
  7: { id: 7, name: 'Spider', baseHP: 60, attack: 22, defense: 14, agility: 20, magicDefense: 32, level: 6, expReward: 66, gilReward: 66 },
  8: { id: 8, name: 'Snake', baseHP: 80, attack: 24, defense: 16, agility: 30, magicDefense: 40, level: 7, expReward: 84, gilReward: 84 },
  9: { id: 9, name: 'Scorpion', baseHP: 96, attack: 28, defense: 20, agility: 26, magicDefense: 36, level: 8, expReward: 144, gilReward: 144 },
  10: { id: 10, name: 'Imp', baseHP: 40, attack: 16, defense: 6, agility: 24, magicDefense: 44, level: 5, expReward: 50, gilReward: 50 },

  // Mid game - moderate stats
  11: { id: 11, name: 'Ogre', baseHP: 200, attack: 35, defense: 20, agility: 10, magicDefense: 40, level: 10, expReward: 195, gilReward: 195 },
  12: { id: 12, name: 'Giant', baseHP: 300, attack: 45, defense: 30, agility: 8, magicDefense: 36, level: 12, expReward: 800, gilReward: 800 },
  13: { id: 13, name: 'Troll', baseHP: 184, attack: 38, defense: 24, agility: 12, magicDefense: 32, level: 11, expReward: 420, gilReward: 420 },
  14: { id: 14, name: 'Vampire', baseHP: 156, attack: 32, defense: 26, agility: 28, magicDefense: 75, level: 14, expReward: 1200, gilReward: 630 },
  15: { id: 15, name: 'Mummy', baseHP: 80, attack: 26, defense: 20, agility: 6, magicDefense: 32, level: 9, expReward: 375, gilReward: 375 },
  16: { id: 16, name: 'Wraith', baseHP: 112, attack: 30, defense: 14, agility: 24, magicDefense: 64, level: 13, expReward: 1002, gilReward: 999 },
  17: { id: 17, name: 'Specter', baseHP: 156, attack: 34, defense: 18, agility: 30, magicDefense: 80, level: 15, expReward: 1455, gilReward: 1455 },
  18: { id: 18, name: 'Mindflayer', baseHP: 112, attack: 36, defense: 24, agility: 32, magicDefense: 120, level: 16, expReward: 2064, gilReward: 2001 },
  19: { id: 19, name: 'Manticore', baseHP: 164, attack: 40, defense: 28, agility: 22, magicDefense: 56, level: 17, expReward: 1068, gilReward: 1068 },
  20: { id: 20, name: 'Chimera', baseHP: 300, attack: 48, defense: 36, agility: 26, magicDefense: 72, level: 18, expReward: 2331, gilReward: 2331 },
  21: { id: 21, name: 'Medusa', baseHP: 68, attack: 24, defense: 22, agility: 40, magicDefense: 100, level: 12, expReward: 699, gilReward: 699 },
  22: { id: 22, name: 'Cockatrice', baseHP: 172, attack: 38, defense: 30, agility: 35, magicDefense: 80, level: 13, expReward: 1455, gilReward: 1455 },
  23: { id: 23, name: 'Gargoyle', baseHP: 80, attack: 28, defense: 50, agility: 18, magicDefense: 64, level: 11, expReward: 196, gilReward: 196 },
  24: { id: 24, name: 'Golem', baseHP: 160, attack: 42, defense: 60, agility: 4, magicDefense: 48, level: 14, expReward: 1455, gilReward: 1455 },
  25: { id: 25, name: 'Dragon Zombie', baseHP: 268, attack: 56, defense: 40, agility: 14, magicDefense: 72, level: 19, expReward: 2598, gilReward: 999 },

  // Late game - high stats
  26: { id: 26, name: 'Blue Dragon', baseHP: 454, attack: 92, defense: 64, agility: 40, magicDefense: 200, level: 22, expReward: 3225, gilReward: 3225 },
  27: { id: 27, name: 'Red Dragon', baseHP: 600, attack: 128, defense: 80, agility: 50, magicDefense: 240, level: 24, expReward: 4446, gilReward: 4446 },
  28: { id: 28, name: 'Green Dragon', baseHP: 352, attack: 68, defense: 56, agility: 36, magicDefense: 160, level: 20, expReward: 3042, gilReward: 3042 },
  29: { id: 29, name: 'Tyrannosaur', baseHP: 600, attack: 110, defense: 70, agility: 28, magicDefense: 180, level: 23, expReward: 5031, gilReward: 5031 },
  30: { id: 30, name: 'Iron Golem', baseHP: 304, attack: 76, defense: 90, agility: 6, magicDefense: 96, level: 21, expReward: 2169, gilReward: 2169 },
  31: { id: 31, name: 'Death Knight', baseHP: 360, attack: 84, defense: 68, agility: 32, magicDefense: 120, level: 25, expReward: 3147, gilReward: 3147 },
  32: { id: 32, name: 'Dark Wizard', baseHP: 200, attack: 50, defense: 40, agility: 42, magicDefense: 200, level: 26, expReward: 3009, gilReward: 3009 },
  33: { id: 33, name: 'Warmech', baseHP: 1000, attack: 164, defense: 128, agility: 64, magicDefense: 200, level: 30, expReward: 32000, gilReward: 32000 },
  34: { id: 34, name: 'Phantom', baseHP: 170, attack: 56, defense: 36, agility: 50, magicDefense: 180, level: 27, expReward: 4710, gilReward: 4710 },
  35: { id: 35, name: 'Death Eye', baseHP: 280, attack: 72, defense: 48, agility: 38, magicDefense: 140, level: 28, expReward: 3225, gilReward: 3225 },
  36: { id: 36, name: 'Evil Eye', baseHP: 162, attack: 52, defense: 40, agility: 34, magicDefense: 130, level: 22, expReward: 3225, gilReward: 3225 },
  37: { id: 37, name: 'Sorcerer', baseHP: 240, attack: 60, defense: 44, agility: 48, magicDefense: 220, level: 29, expReward: 3009, gilReward: 3009 },
  38: { id: 38, name: 'Demon', baseHP: 370, attack: 96, defense: 72, agility: 44, magicDefense: 160, level: 28, expReward: 3654, gilReward: 3654 },

  // Bosses - extreme stats
  39: { id: 39, name: 'Lich', baseHP: 400, attack: 60, defense: 40, agility: 30, magicDefense: 180, level: 20, expReward: 2000, gilReward: 2000 },
  40: { id: 40, name: 'Kary', baseHP: 600, attack: 90, defense: 60, agility: 60, magicDefense: 180, level: 25, expReward: 2500, gilReward: 2500 },
  41: { id: 41, name: 'Kraken', baseHP: 800, attack: 120, defense: 70, agility: 45, magicDefense: 200, level: 30, expReward: 3500, gilReward: 3500 },
  42: { id: 42, name: 'Tiamat', baseHP: 1000, attack: 140, defense: 90, agility: 80, magicDefense: 240, level: 35, expReward: 5000, gilReward: 5000 },
  43: { id: 43, name: 'Chaos', baseHP: 2000, attack: 200, defense: 100, agility: 100, magicDefense: 255, level: 40, expReward: 0, gilReward: 0 },
};

// ============================================================================
// Encounter System
// ============================================================================

export class EncounterSystem {
  private stepsSinceEncounter: number = 0;
  private stepsUntilEncounter: number = 0;
  private lastEncounterZone: string | null = null;

  constructor() {
    this.resetEncounterCounter();
  }

  /**
   * Calculate encounter chance based on terrain and zone
   * Returns true if an encounter should trigger
   */
  checkEncounter(terrain: TerrainType, x: number, y: number, forceCheck: boolean = false): boolean {
    // Check if terrain has encounters
    const baseRate = TERRAIN_ENCOUNTER_RATES[terrain];
    if (baseRate === 0) {
      return false; // Safe terrain
    }

    // Increment step counter
    this.stepsSinceEncounter++;

    // Check if we've reached the encounter threshold
    if (this.stepsSinceEncounter >= this.stepsUntilEncounter || forceCheck) {
      // Get encounter zone for this terrain/location
      const zone = getEncounterZoneForTerrain(terrain, x, y);
      if (!zone) {
        return false;
      }

      // Calculate final encounter chance
      const encounterZone = ENCOUNTER_ZONES[zone];
      if (!encounterZone) {
        return false;
      }

      // Apply zone encounter rate modifier
      const zoneModifier = encounterZone.encounterRate / 10;
      const finalRate = baseRate / zoneModifier;

      // Random variance (±20% from base rate)
      const variance = 0.8 + Math.random() * 0.4;
      const adjustedSteps = Math.floor(finalRate * variance);

      // Trigger encounter!
      if (this.stepsSinceEncounter >= adjustedSteps || forceCheck) {
        this.resetEncounterCounter();
        this.lastEncounterZone = zone;
        return true;
      }
    }

    return false;
  }

  /**
   * Select a random enemy group from the current encounter zone
   * Uses weighted random selection
   */
  selectEnemyGroup(zone: string): number[] {
    const encounterZone = ENCOUNTER_ZONES[zone];
    if (!encounterZone || !encounterZone.enemyGroups.length) {
      // Fallback to a basic enemy group
      return [1]; // Single goblin
    }

    // Calculate total weight
    const totalWeight = encounterZone.weights.reduce((sum, w) => sum + w, 0);

    // Random weighted selection
    let random = Math.random() * totalWeight;
    for (let i = 0; i < encounterZone.enemyGroups.length; i++) {
      const weight = encounterZone.weights[i] ?? 1;
      random -= weight;
      if (random <= 0) {
        return encounterZone.enemyGroups[i] ?? [1];
      }
    }

    // Fallback (shouldn't reach here)
    return encounterZone.enemyGroups[0] ?? [1];
  }

  /**
   * Generate enemy instances for battle from enemy IDs
   * Creates BattleEnemy objects with stats, HP, sprites, etc.
   */
  generateEnemies(enemyIds: number[]): BattleEnemy[] {
    const enemies: BattleEnemy[] = [];

    enemyIds.forEach((enemyId, _index) => {
      const template = ENEMY_TEMPLATES[enemyId];
      if (!template) {
        console.warn(`[EncounterSystem] Unknown enemy ID: ${enemyId}`);
        return;
      }

      // Get sprite configuration
      const spriteConfig = getEnemySprite(enemyId);
      const spriteId = spriteConfig ? `enemy_${enemyId}` : 'enemy_fallback';

      // Calculate HP with slight variance (±10%)
      const hpVariance = 0.9 + Math.random() * 0.2;
      const hp = Math.floor(template.baseHP * hpVariance);

      // Create battle enemy instance with full combat stats
      const enemy: BattleEnemy = {
        id: enemyId,
        name: template.name,
        hp,
        maxHP: hp,
        attack: template.attack,
        defense: template.defense,
        agility: template.agility,
        magicDefense: template.magicDefense,
        x: 0, // Position will be set by BattleScene
        y: 0,
        spriteId,
        dead: false,
      };

      enemies.push(enemy);
    });

    return enemies;
  }

  /**
   * Generate a boss encounter (non-random, triggered by location)
   */
  generateBossEncounter(bossId: number): BattleEnemy[] {
    const template = ENEMY_TEMPLATES[bossId];
    if (!template) {
      console.error(`[EncounterSystem] Unknown boss ID: ${bossId}`);
      return [];
    }

    const spriteConfig = getEnemySprite(bossId);
    const spriteId = spriteConfig ? `enemy_${bossId}` : 'enemy_fallback';

    // Bosses have full HP and stats, no variance
    const enemy: BattleEnemy = {
      id: bossId,
      name: template.name,
      hp: template.baseHP,
      maxHP: template.baseHP,
      attack: template.attack,
      defense: template.defense,
      agility: template.agility,
      magicDefense: template.magicDefense,
      x: 0,
      y: 0,
      spriteId,
      dead: false,
    };

    return [enemy];
  }

  /**
   * Calculate total exp and gil rewards from defeated enemies
   */
  calculateRewards(defeatedEnemies: BattleEnemy[]): { exp: number; gil: number } {
    let totalExp = 0;
    let totalGil = 0;

    defeatedEnemies.forEach(enemy => {
      const template = ENEMY_TEMPLATES[enemy.id];
      if (template) {
        totalExp += template.expReward;
        totalGil += template.gilReward;
      }
    });

    return { exp: totalExp, gil: totalGil };
  }

  /**
   * Reset the encounter counter with a new random threshold
   */
  private resetEncounterCounter(): void {
    // FF1 uses a range of steps (typically 15-30)
    this.stepsUntilEncounter = Math.floor(Math.random() * 16) + 15;
    this.stepsSinceEncounter = 0;
  }

  /**
   * Get current step count (for debug/UI)
   */
  getStepCount(): { current: number; target: number } {
    return {
      current: this.stepsSinceEncounter,
      target: this.stepsUntilEncounter,
    };
  }

  /**
   * Get the last encounter zone (for context)
   */
  getLastEncounterZone(): string | null {
    return this.lastEncounterZone;
  }

  /**
   * Force reset encounter counter (for after battles, etc.)
   */
  reset(): void {
    this.resetEncounterCounter();
    this.lastEncounterZone = null;
  }

  /**
   * Check if player should flee based on terrain
   * Swamps and dungeons are harder to flee from
   */
  getFleeChance(terrain: TerrainType, partyLevel: number, enemyLevel: number): number {
    // Base flee chance
    let baseChance = 0.5;

    // Terrain modifiers
    switch (terrain) {
      case TerrainType.SWAMP:
        baseChance *= 0.5; // Harder to flee in swamps!
        break;
      case TerrainType.CAVE:
      case TerrainType.SHRINE:
        baseChance *= 0.6; // Dungeons are dangerous
        break;
      case TerrainType.ROAD:
      case TerrainType.BRIDGE:
        baseChance *= 1.5; // Easier to flee on safe paths
        break;
    }

    // Level difference modifier
    const levelDiff = partyLevel - enemyLevel;
    baseChance += levelDiff * 0.05;

    // Clamp between 0.1 and 0.9
    return Math.max(0.1, Math.min(0.9, baseChance));
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get encounter rate for a terrain type (for display/debug)
 */
export function getTerrainEncounterRate(terrain: TerrainType): number {
  return TERRAIN_ENCOUNTER_RATES[terrain] ?? 0;
}

/**
 * Check if terrain has random encounters
 */
export function hasRandomEncounters(terrain: TerrainType): boolean {
  return TERRAIN_ENCOUNTER_RATES[terrain] > 0;
}

/**
 * Get enemy template by ID (for UI/display)
 */
export function getEnemyTemplate(enemyId: number): EnemyTemplate | null {
  return ENEMY_TEMPLATES[enemyId] ?? null;
}
